import { characters } from "@/mocks/characters";
import { episodes } from "@/mocks/episodes";
import { newsList } from "@/mocks/news";

export interface Ability {
  name: string;
  desc: string;
}

export interface Character {
  id: string;
  name: string;
  nameJp: string;
  epithet: string;
  category: string;
  village: string;
  rank: string;
  ninjaType: string;
  avatar: string;
  intro: string;
  bio: string[];
  abilities: Ability[];
  relations: string[];
  tags: string[];
}

export interface Episode {
  id: string;
  number: number;
  title: string;
  arc: string;
  cover: string;
  summary: string;
  story: string[];
  characters: string[];
  duration: string;
  airDate: string;
}

export interface NewsItem {
  id: string;
  title: string;
  date: string;
  category: string;
  cover: string;
  summary: string;
  content: string[];
}

export const allCharacters = characters as Character[];
export const allEpisodes = episodes as Episode[];
export const allNews = newsList as NewsItem[];

export function getCharacterById(id?: string): Character | undefined {
  if (!id) return undefined;
  return allCharacters.find((item) => item.id === id);
}

export function getEpisodeById(id?: string): Episode | undefined {
  if (!id) return undefined;
  return allEpisodes.find((item) => item.id === id);
}

export function getNewsById(id?: string): NewsItem | undefined {
  if (!id) return undefined;
  return allNews.find((item) => item.id === id);
}

export function getRelatedCharacters(character?: Character, count = 4): Character[] {
  if (!character) return [];
  const byRelation = character.relations
    .map((rid) => getCharacterById(rid))
    .filter((c): c is Character => Boolean(c));
  const fillers = allCharacters.filter(
    (c) => c.id !== character.id && c.category === character.category,
  );
  const merged = [...byRelation, ...fillers].filter(
    (c, index, arr) => c.id !== character.id && arr.findIndex((x) => x.id === c.id) === index,
  );
  return merged.slice(0, count);
}

export function getRelatedEpisodes(episode?: Episode, count = 4): Episode[] {
  if (!episode) return [];
  const sameArc = allEpisodes.filter((e) => e.id !== episode.id && e.arc === episode.arc);
  const others = allEpisodes.filter((e) => e.id !== episode.id && e.arc !== episode.arc);
  return [...sameArc, ...others].slice(0, count);
}

export function getRelatedNews(item?: NewsItem, count = 3): NewsItem[] {
  if (!item) return [];
  const sameCat = allNews.filter((n) => n.id !== item.id && n.category === item.category);
  const others = allNews.filter((n) => n.id !== item.id && n.category !== item.category);
  return [...sameCat, ...others].slice(0, count);
}

export function searchAll(keyword: string) {
  const q = keyword.trim().toLowerCase();
  if (!q) {
    return { episodes: [], characters: [], news: [], total: 0 };
  }
  const matchedEpisodes = allEpisodes.filter(
    (e) =>
      e.title.toLowerCase().includes(q) ||
      e.summary.toLowerCase().includes(q) ||
      String(e.number) === q,
  );
  const matchedCharacters = allCharacters.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.nameJp.toLowerCase().includes(q) ||
      c.epithet.toLowerCase().includes(q),
  );
  const matchedNews = allNews.filter(
    (n) => n.title.toLowerCase().includes(q) || n.summary.toLowerCase().includes(q),
  );
  return {
    episodes: matchedEpisodes,
    characters: matchedCharacters,
    news: matchedNews,
    total: matchedEpisodes.length + matchedCharacters.length + matchedNews.length,
  };
}

export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function totalPages(count: number, pageSize: number): number {
  return Math.max(1, Math.ceil(count / pageSize));
}

export function formatDate(date: string): string {
  return date.replace(/-/g, ".");
}